import type { Theme } from 'foundations-theming-base/types';
import type { ThemeSettings, ThemeVersion } from './getLocalThemeSettings';
import { MODERN_THEMES } from 'foundations-theming-base/modern-themes';
import type { ModernThemeKey } from 'foundations-theming-base/modern-themes';
import { getHostname } from './utils';

type ModernTheme = (typeof MODERN_THEMES)[ModernThemeKey];
type ChosenTheme =
  | 'canvas-light'
  | 'trellis-light'
  | 'trellis-dark'
  | ModernThemeKey;
type MultiTheme = Record<ChosenTheme, Theme>;
type CanvasTheme = {
  themeName: 'canvas';
  themeMode: 'light';
};

type TrellisTheme = {
  themeName: 'trellis';
  themeMode: 'light' | 'dark';
};

type SparseTheme = {
  themeName: ModernTheme['themeName'];
  themeMode: ModernTheme['themeMode'];
};

type ThemeParams = (CanvasTheme | TrellisTheme | SparseTheme) & {
  themeVersion: ThemeVersion;
};

const MODERN_THEME_NAMES: string[] = Object.values(MODERN_THEMES).map(
  ({ themeName }) => themeName
);

function validateThemeParams(themeParams: {
  themeName: string;
  themeMode: string;
  themeVersion: ThemeVersion;
}): asserts themeParams is ThemeParams {
  const { themeName, themeMode } = themeParams;
  if (!['canvas', 'trellis', ...MODERN_THEME_NAMES].includes(themeName)) {
    throw new Error(`Invalid theme name: ${themeName}`);
  }
  if (!['light', 'dark'].includes(themeMode)) {
    throw new Error(`Invalid theme mode: ${themeMode}`);
  }
  if (themeName === 'canvas' && themeMode === 'dark') {
    throw new Error(`Canvas theme does not support dark mode`);
  }
  if (
    MODERN_THEME_NAMES.includes(themeName) &&
    !(`${themeName}-${themeMode}` in MODERN_THEMES)
  ) {
    throw new Error(`${themeName} theme does not support ${themeMode} mode`);
  }
}

// id rather not add an enviro dep, so get the hublet manually
export function getUrlDetails(themeVersion: ThemeVersion) {
  const regexp = /^(?:app|local)(?:-([a-z]+[0-9]))?.hubspot(qa)?\.com/;
  const baseUrl = getHostname();
  const match = baseUrl.match(regexp);
  if (!match) {
    return { hublet: '', suffix: '' };
  }
  const hublet = match[1] || '';

  let suffix = match[2] || '';
  if (themeVersion.type === 'deployed') {
    suffix = themeVersion?.env === 'qa' ? 'qa' : '';
  }

  return { hublet, suffix };
}

const getDeployedThemeUrl = (themeVersion: ThemeVersion) => {
  const { hublet, suffix } = getUrlDetails(themeVersion);

  if (themeVersion.type === 'default') {
    throw new Error(`${themeVersion.type} themes do not have deployed URLs.`);
  }

  if (themeVersion.type === 'version') {
    return `https://static.hsappstatic.net/foundations-theming-base/static-${themeVersion.version}/bundles/multi-theme-${themeVersion.channel}.js`;
  }

  const subdomain =
    themeVersion.type === 'local' ? `local${hublet}` : `app${hublet}`;

  return `https://${subdomain}.hubspot${suffix}.com/foundations-theming-base/theme-${themeVersion.channel}.js`;
};

export const getDeployedTheme = async (themeSettings: ThemeSettings) => {
  validateThemeParams(themeSettings);
  const { themeName, themeMode, themeVersion } = themeSettings;
  const chosenTheme = `${themeName}-${themeMode}` as ChosenTheme;
  const themeUrl = getDeployedThemeUrl(themeVersion);

  const deployedTheme: MultiTheme = await import(
    /* webpackIgnore: true */ themeUrl
  )
    .then((m) => m.default)
    .catch((err) => {
      throw new Error(`Failed to load theme from ${themeUrl}: ${err.message}`);
    });

  const theme = deployedTheme[chosenTheme];
  if (!theme) {
    throw new Error('Unsupported theme name and mode combination.');
  }

  return theme;
};
